import { useParams } from 'react-router';
import { useMutation } from '@apollo/client';
import { useSetRecoilState } from 'recoil';

import { apolloClientContracts } from '@/apollo/clients/contracts.apollo';
import { ALL_ITEMS } from '@/config/common.config';
import { TerminateContractLines, TerminateContractLinesVariables } from
  '@/graphql/contracts/_generated_/terminateContractLines.mutation';
import TerminateContractLinesMutation from '@/graphql/contracts/terminateContractLines.mutation.gql';
import { rightsidePanel_isOpen } from '../../../../RightPanel.state';
import { notificationService } from '@/services/notification/notification.client';
import GetContractDetailsQuery from '@/graphql/contracts/getContractDetails.query.gql';
import {
  clearLinesSelection,
  useSelectedLines,
  triggerLineRefresh
} from '@/pages/ContractDetails/ContractLines/components/ContractLinesGrid/linesSelectionObservable';
import GetBCContractLinesQuery from '@/graphql/contracts/getPaginatedFilteredBCContractLines.query.gql';
import GetPaginatedFilteredVariableContractLinesQuery from
  '@/graphql/contracts/getPaginatedFilteredVariableContractLines.query.gql';
import { triggerVariableRefresh } from
  '@/pages/ContractDetails/ContractLines/components/ContractLinesGrid/variableLinesSelectionObservable';
import {
  ContractLineBillingCycleOutput,
  ContractStatusEnum
} from '@/graphql/contracts/_generated_/contracts';

export type TerminateContractLinesDto = {
  terminationDate: Date;
  reasonCode: string;
  comment: string;
};

export default function useTerminateContractLines() {
  const { contractId }: { contractId: UUID } = useParams();
  const selectedOrdinaryLineRows = useSelectedLines();
  const statuses = [ContractStatusEnum.Active, ContractStatusEnum.ActiveDNR];
  const selectedLines = selectedOrdinaryLineRows?.filter((line) =>
    statuses.includes(((line.payload.contractLine) as unknown as ContractLineBillingCycleOutput).status!));
  const setIsOpenRightPanel = useSetRecoilState(rightsidePanel_isOpen);
  const [_terminateContractLines, { loading, error }] =
    useMutation<TerminateContractLines, TerminateContractLinesVariables>(
      TerminateContractLinesMutation,
      {
        client: apolloClientContracts,
        awaitRefetchQueries: true,
        refetchQueries: [
          {
            query: GetContractDetailsQuery,
            variables: { contractId }
          },
          selectedOrdinaryLineRows.length ?
            {
              query: GetBCContractLinesQuery,
              variables: {
                filter: {
                  contractId: { eq: contractId }
                },
                first: ALL_ITEMS
              }
            } :
            {
              query: GetPaginatedFilteredVariableContractLinesQuery,
              variables: {
                filter: { headerPrimaryKey: { eq: contractId } },
                first: ALL_ITEMS,
              }
            }
        ],
        onCompleted: () => {
          clearLinesSelection();
          setIsOpenRightPanel(false);
          triggerLineRefresh();
          triggerVariableRefresh();
          notificationService.open({
            title: 'Contract Line Status Saved',
            text: 'The chosen contract lines have been changed',
            type: 'success'
          });
        },
        onError: (err) => notificationService.open({
          title: 'Error',
          text: err.message,
          type: 'danger'
        }),
      }
    );

  const terminateContractLines = (params: TerminateContractLinesDto) => {
    _terminateContractLines({
      variables: {
        ...params,
        contractId,
        contractLines: selectedLines.map(({ id }) => ({ contractLineID: id })),
      }
    });
  };

  return {
    terminateContractLines,
    isLoadingTerminate: loading,
    errorTerminate: error
  } as const;
}
